import { Trans, useTranslation } from 'react-i18next';
import { IconBaseProps } from 'react-icons';
import { HiOutlineGlobeAlt, HiOutlineLocationMarker } from 'react-icons/hi';
import { useNavigate } from 'react-router';
import AsideSection from '@src/components/AsideSection';
import FeatureSection from '@src/components/FeatureSection';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import { NifOrderStepPageWithOptionalOrderProps } from '../types/stepProps';

function BirthCertificateIcon(props: IconBaseProps): JSX.Element {
  return (
    <svg
      width="20"
      height="26"
      viewBox="0 0 20 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M3.57136 24.6163H16.4282C17.8483 24.6163 18.9995 23.474 18.9995 22.065V9.83701C18.9995 9.49869 18.8641 9.17422 18.6229 8.935L11.662 2.02841C11.4209 1.78918 11.0939 1.65479 10.7529 1.65479H3.57136C2.15124 1.65479 1 2.79703 1 4.20606V22.065C1 23.474 2.15124 24.6163 3.57136 24.6163Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.1951 10.8738C12.1951 12.3286 11.0158 13.5079 9.56104 13.5079C8.10628 13.5079 6.92696 12.3286 6.92696 10.8738C6.92696 9.41906 8.10628 8.23975 9.56104 8.23975C11.0158 8.23975 12.1951 9.41906 12.1951 10.8738Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.56104 14.8249C7.3789 14.8249 5.60992 16.5939 5.60992 18.776V19.4346H13.5121V18.776C13.5121 16.5939 11.7432 14.8249 9.56104 14.8249Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NifPrepareDocumentsPage({
  previousUrl,
  getNextUrl,
  nifOrder,
}: NifOrderStepPageWithOptionalOrderProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['nif']);

  return (
    <div className="space-y-8 md:pr-20">
      <div className="space-y-2">
        <h2>{t('prepare_documents.title', 'Prepare your documents')}</h2>
        <p>
          {t(
            'prepare_documents.subtitle',
            'You will need the following documents to complete this form.',
          )}
        </p>
      </div>
      <FeatureSection
        Icon={HiOutlineGlobeAlt}
        name={t('prepare_documents.identity.title', 'Proof of identity')}
      >
        {t(
          'prepare_documents.identity.body',
          "A high-quality image of the applicant's passport or EU National ID card.",
        )}
      </FeatureSection>
      <FeatureSection
        Icon={HiOutlineLocationMarker}
        name={t('prepare_documents.address.title', 'Proof of address')}
      >
        {t(
          'prepare_documents.address.body',
          "This can be the first page of a bank statement, utility bill, or driver's license.",
        )}
      </FeatureSection>
      <AsideSection>
        <div className="space-y-4">
          <h4>
            {t(
              'prepare_documents.minor.subtitle',
              'If requesting a NIF for a minor under the age of 18:',
            )}
          </h4>
          <FeatureSection
            Icon={BirthCertificateIcon}
            name={t(
              'prepare_documents.minor_birth.title',
              "Minor's birth certificate",
            )}
          >
            {t(
              'prepare_documents.minor_birth.body',
              'An adoption certificate is an acceptable substitute for a birth certificate.',
            )}
          </FeatureSection>
          <FeatureSection
            Icon={HiOutlineGlobeAlt}
            name={t(
              'prepare_documents.minor_parent_passport.title',
              "Parents' Passports",
            )}
          >
            {t(
              'prepare_documents.minor_parent_passport.body',
              "If the minor is being represented by a single parent, then only one parent's passport is required.",
            )}
          </FeatureSection>
        </div>
      </AsideSection>
      <AsideSection>
        <div className="space-y-4">
          <h4>
            {t(
              'prepare_documents.pt_citizenship.subtitle',
              'If the applicant was born in Portugal or holds Portuguese citizenship:',
            )}
          </h4>
          <FeatureSection
            Icon={BirthCertificateIcon}
            name={t(
              'prepare_documents.pt_citizenship.title',
              'Portuguese birth certificate',
            )}
          >
            <Trans
              t={t}
              i18nKey="prepare_documents.pt_citizenship.body"
              defaults="If the applicant does not have one, you can order one <a>here</a>."
              components={{
                a: (
                  // we escape this content to pass it through the string;
                  // it definitely *has* content
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href="https://www.bordr.com/birth-certificate"
                    aria-label={t(
                      'prepare_documents.pt_citizenship.birth_certs_sr',
                      'Birth certificates at Bordr',
                    )}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </FeatureSection>
        </div>
      </AsideSection>
      <OrderNavigationButtons
        previousUrl={previousUrl}
        onClick={() => navigate(getNextUrl(nifOrder))}
      />
    </div>
  );
}

export default NifPrepareDocumentsPage;
